import { AnsweringClient } from "../clients/answeringClient";
import { AuthRetry, SessionExpiredError } from "../utils/authRetry";

export interface IConsentTerm {
  type: string;
  title: string;
  description: string;
  allow_audio: boolean;
}

export interface ConsentControllerDelegate {
  didAnswerConsent(): void;
  didGetConsentTerm(term: IConsentTerm): void;
  didGetAssignmentToken(token: string): void;
  applicationShouldLogout(): void;
  genericError(message?: string): void;
}

export class ConsentController {
  private readonly auth: AuthRetry;

  constructor(
    private delegate: ConsentControllerDelegate,
    private readonly answeringClient = new AnsweringClient()
  ) {
    this.auth = new AuthRetry(this.answeringClient, () =>
      this.delegate.applicationShouldLogout()
    );
  }

  public async getConsent(accessToken: string, assignmentToken: string) {
    try {
      const response = await this.auth.run(accessToken, (token) =>
        this.answeringClient.getConsentTerm(token, assignmentToken)
      );
      this.delegate.didGetConsentTerm(response.data);
    } catch (error: any) {
      this.reportError(error);
    }
  }

  public async answerConsent(
    accessToken: string,
    assignmentToken: string,
    acceptedConsent: boolean
  ) {
    try {
      await this.auth.run(accessToken, (token) =>
        this.answeringClient.answerConsent(
          token,
          assignmentToken,
          acceptedConsent
        )
      );
      this.delegate.didAnswerConsent();
    } catch (error: any) {
      this.reportError(error);
    }
  }

  public async getAssignmentToken(accessToken: string, instrumentId: string) {
    try {
      const response = await this.auth.run(accessToken, (token) =>
        this.answeringClient.getInstrumentToken(token, instrumentId)
      );
      this.delegate.didGetAssignmentToken(response.data.token);
    } catch (error: any) {
      this.reportError(error);
    }
  }

  /** Swallows SessionExpiredError rather than reporting it; see its declaration. */
  private reportError(error: any): void {
    if (error instanceof SessionExpiredError) {
      return;
    }
    this.delegate.genericError(this.extractErrorMessage(error));
  }

  private extractErrorMessage(error: any): string | undefined {
    return (
      error?.response?.data?.message ||
      error?.response?.data?.error ||
      error?.message
    );
  }
}
